/**
 * Уведомление о сборе аналитики.
 *
 * Зачем: с переездом RU-инстанса на Яндекс.Метрику мы начали писать сессии
 * Вебвизором. Человек должен об этом знать — на лендинге такой баннер есть с
 * июня, в приложении не было, а собираем мы именно здесь.
 *
 * Это notice, а не запрос разрешения: аналитика работает сразу, баннер
 * информирует и запоминает, что его прочитали. Тот же выбор, что и на
 * лендинге (не Consent Mode) — иначе половина продуктовых метрик исчезнет
 * вместе с непрожатыми кнопками, а нам они нужны для работы над сервисом,
 * не для рекламы.
 *
 * ⚠️ Namespace `analyticsConsent`, а не `consent`: последний давно занят
 * согласием на доступ администраторов к документам Сейфа. Два разных
 * согласия в одной секции — гарантированная путаница при переводе.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { track } from '../analytics';
import { activeProviders } from '../analytics/core';
import { BASE_DOMAIN, PRIVACY_URL } from '../branding';

const ACK_KEY = 'analytics_consent_ack';

// Ссылка на политику — PRIVACY_URL из branding.js (на vsenapole.ru это
// /polyconf на Tilda; раньше здесь собирался ${BRAND_URL}/privacy, которого на
// Tilda нет, и «Подробнее» вело в 404).

// ⚠️ localStorage привязан к origin: app.fixty.io, <тенант>.fixty.io и страница
// инвайта — разные хранилища, и после каждого перехода между ними баннер
// появлялся заново. Признак «прочитано» дублируем кукой на родительском домене
// (.fixty.io / .vsenapole.ru) — она общая для всех поддоменов.
const ACK_COOKIE = 'analytics_consent_ack';
const ACK_MAX_AGE = 60 * 60 * 24 * 365;

function cookieDomainAttr() {
  const host = window.location.hostname;
  return host === BASE_DOMAIN || host.endsWith(`.${BASE_DOMAIN}`)
    ? `; domain=.${BASE_DOMAIN}`
    : '';
}

function readAck() {
  // localStorage бросает в приватных окнах и при запрете хранилища — там
  // баннер просто покажется снова, это лучше, чем белый экран.
  try {
    if (localStorage.getItem(ACK_KEY) === '1') return true;
  } catch { /* см. выше */ }
  try {
    return document.cookie.split('; ').some((c) => c === `${ACK_COOKIE}=1`);
  } catch {
    return false;
  }
}

function writeAck() {
  try {
    localStorage.setItem(ACK_KEY, '1');
  } catch {
    // Не сохранилось — покажем в следующий раз. Скрыть сейчас всё равно надо:
    // человек нажал кнопку и ждёт реакции.
  }
  try {
    document.cookie = `${ACK_COOKIE}=1; max-age=${ACK_MAX_AGE}; path=/${cookieDomainAttr()}; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
  } catch { /* куки запрещены — остаётся localStorage */ }
}

export default function AnalyticsConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Нечего собирать — не о чем и уведомлять. На сборке без аналитики
    // (REACT_APP_ANALYTICS=none и в dev) баннер был бы враньём.
    const providers = activeProviders();
    if (!providers.length || providers.every((p) => p === 'noop')) return;
    if (readAck()) return;

    setVisible(true);
    track('analytics_consent_shown');
  }, []);

  if (!visible) return null;

  // Метрика пишет сессии Вебвизором — называем её прямо: человек должен
  // понимать, кто именно записывает его действия.
  const textKey = activeProviders().includes('metrika')
    ? 'analyticsConsent.textMetrika'
    : 'analyticsConsent.text';

  const accept = () => {
    writeAck();
    track('analytics_consent_accepted');
    setVisible(false);
  };

  return (
    <div
      data-ph-capture-attribute-component="analytics_consent_banner"
      style={{
        position: 'fixed',
        left: 'max(12px, env(safe-area-inset-left))',
        right: 'max(12px, env(safe-area-inset-right))',
        bottom: 'max(12px, env(safe-area-inset-bottom))',
        zIndex: 9000,
        maxWidth: 560,
        margin: '0 auto',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        borderRadius: 12,
        background: '#1e2535',
        color: '#e8eaf0',
        border: '1px solid #2a3347',
        boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
        fontSize: 13,
        lineHeight: 1.45,
      }}
    >
      <span style={{ flex: '1 1 260px' }}>
        {t(textKey)}
        {PRIVACY_URL ? (
          <>
            {' '}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-ph-capture-attribute-action="privacy_policy"
              onClick={() => track('analytics_consent_learn_more')}
              style={{ color: '#7aa2f7', textDecoration: 'underline' }}
            >
              {t('analyticsConsent.learnMore')}
            </a>
          </>
        ) : null}
      </span>

      <button
        type="button"
        onClick={accept}
        data-ph-capture-attribute-action="accept"
        style={{
          flex: '0 0 auto',
          padding: '8px 18px',
          borderRadius: 8,
          border: 'none',
          background: '#3b82f6',
          color: '#fff',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {t('analyticsConsent.accept')}
      </button>
    </div>
  );
}
